// El dibujo de la red en el Registro Público de Comercio (el grafo de Nexo), como datos y reglas puras. Etapa 4, ajustes 4d (2026-10-05):
// lo que era la parte pura de app/nexo-frontend/src/ui/GrafoSiger.tsx pasó aquí para que Argus Team pinte el mismo grafo sin copiarlo.
// Cada aplicación lo pinta con su React y sus colores (`TonoRed` es una ficha, no un color). Sin React, sin DOM y sin llamadas a la red.
//
// La forma de los datos es la de nexo.grafo_red (migración 20261005120000): public.nexo_viz_entidad_grafo_siger en Nexo y la llave
// `grafo` de nexo.red (argus.red/1) en Argus Team. Rojo de identidad sólo con identidad corroborada (rojoDeIdentidad); una relación con
// un ancla sin corroborar lleva la marca «posible» (decisión de Bernardo del 2026-10-04, la del buscador).
import { anchoChip, colocarChips } from './red-chips.ts';
import { rojoDeIdentidad } from './red-senales.ts';
import { MARCA_POSIBLE, MARCA_POSIBLE_AYUDA } from '../rotulos/buscador.ts';

/* ------------------------------------------------------------ la forma ------------------------------------------------------------ */

export interface ValorIdentidadSiger {
  valor: string | number;
  origen?: string;
  precision?: string;
  tipo?: string;
  expediente?: string;
  archivo?: string;
  pag?: number;
  cita?: string;
}
export interface OfacDeActor {
  entidad_id: string;
  nombre: string;
  nivel: 'confirmada' | 'alta' | 'media' | 'contradictoria';
  /** nexo.identidad_corroborada (desde 20261005120000). Sin el campo, rojoDeIdentidad usa su respaldo. */
  corroborada?: boolean | null;
  senales: string[];
  contradicciones: string[];
  /** Banderas de revisión: no cambian el nivel (ausentes en corridas anteriores a 2026-09-24 r5). */
  revision?: string[];
  metodo: string;
  /** Revisión 4d (I3): si esa persona o empresa de la lista sigue en ella, salió o no está (nexo.ofac_vigencia). */
  en_lista?: EstadoListaRed | null;
}
/** Si alguien sigue en la lista de OFAC, salió o no está (nexo.ofac_vigencia). */
export type EstadoListaRed = 'en_lista' | 'retirada' | 'no_esta';
export interface FlagSatSiger {
  lista: string;
  categoria: string;
  supuesto?: string | null;
  fecha?: string | null;
  /** rfc y denominacion: coincidencia; rfc_raiz_nombre: posible, falta confirmar con el RFC completo. */
  metodo: 'rfc' | 'denominacion' | 'rfc_raiz_nombre' | 'rfc_ofac';
  rfc?: string | null;
}
export interface RelacionOfacSiger {
  contraparte_id: string;
  nombre: string;
  tipo: string;
  rol: string;
  saliente: boolean;
  /** La persona o empresa del registro que es esa contraparte (sólo con identidad corroborada, desde 20261005120000). */
  actor_id: string | null;
  /** Revisión 4d (I3): si la contraparte sigue en la lista, salió o no está. */
  en_lista?: EstadoListaRed | null;
}
export interface ActorGrafoSiger {
  tipo: string;
  nombre: string;
  variantes: string[];
  expedientes: string[];
  identidad: Record<string, ValorIdentidadSiger[]>;
  alertas: { tipo: string; texto?: string; campo?: string; valor?: string; motivo?: string }[];
  ofac: OfacDeActor[];
  sat?: FlagSatSiger[];
  propiedad: { fecha_lista?: string; archivo?: string; total_acciones?: number; calidad_lista?: string; pct_ofac?: number | null } | null;
}
export interface EvidenciaLazo {
  fecha?: string | null;
  accion?: string | null;
  rol_literal?: string | null;
  archivo?: string | null;
  pag?: number | null;
  cita?: string | null;
  cita_verificada?: boolean;
  acciones?: number | null;
  /** Para lazos que vienen de una relación dentro de la sociedad (compraventa, sucesión, poder): la otra parte. */
  contraparte?: string | null;
  detalle?: string | null;
}
export type LazoSiger =
  | { tipo: 'participacion'; rol: string; control: boolean; desde: string | null; hasta: string | null; estado: string; evidencia: EvidenciaLazo[]; persona: string; sociedad: string }
  | { tipo: 'relacion'; relacion: string; detalle?: string | null; base: string; fecha?: string | null; evidencia: EvidenciaLazo[]; de: string; a: string };
export interface VinculoSiger {
  actor_id: string;
  ancla_actor_id: string;
  fuerza: number;
  aristas: number;
  camino: LazoSiger[];
  vigente: boolean | null;
  incluye_control: boolean | null;
  relacion_personal: boolean | null;
  posterior_listado: boolean | null;
  pct_ofac: number | null;
  pct_ofac_total: number | null;
  actor_es_ofac: boolean;
  /** 2026-10-02: el tipo de relación (nexo.siger_tipo_liga), el mismo de la lista «Registro de comercio», con su texto y su peso. */
  tipo?: string;
  tipo_texto?: string | null;
  tipo_explicacion?: string | null;
  tipo_orden?: number;
  /** 20261005120000: ¿la identidad del ancla con la raíz está corroborada? `false`: la relación es «posible». */
  ancla_corroborada?: boolean;
}
export interface AnclaSiger {
  actor_id: string;
  nivel: string;
  /** nexo.identidad_corroborada (desde 20261005120000). */
  corroborada?: boolean | null;
  senales: string[];
  contradicciones: string[];
  revision?: string[];
  metodo: string;
  estado: string;
}
export interface GrafoSiger {
  run: { id: string; extractor: string; reglas: string; creado: string };
  aviso: string;
  /** 2026-10-02: ligas por tipo, en orden de peso; los mismos conteos que la lista «Registro de comercio». */
  totales_tipo?: { tipo: string; texto: string | null; explicacion?: string | null; orden: number; ligas: number }[] | null;
  relaciones_ofac?: RelacionOfacSiger[];
  /** Listas SAT del RFC que publica OFAC para esta entidad (cruce previo nexo.cruce_lista). */
  sat_entidad?: FlagSatSiger[];
  anclas: AnclaSiger[];
  vinculos: VinculoSiger[];
  actores: Record<string, ActorGrafoSiger>;
}

/** De dónde sale la raíz del dibujo de nexo.red: es la persona o empresa misma, su identidad corroborada, con quien se relaciona por las
 * actas o su identidad sin corroborar. */
export type MotivoRaiz = 'es_el_sujeto' | 'identidad' | 'relacion' | 'posible';
export interface RaizRed { entidadId: string; nombre: string; motivo: MotivoRaiz; enLista: 'en_lista' | 'retirada' | 'no_esta' | null }
/** nexo.red (contrato argus.red/1, migración 20261005120000). */
export interface RespuestaRed {
  contrato: 'argus.red/1';
  sujeto: { ref: string; nombre: string; tipo: string };
  raiz: RaizRed | null;
  raices: RaizRed[];
  seleccionado: string | null;
  grafo: GrafoSiger | null;
  recorte: { relaciones: number; mostradas: number; limite: number; citasAcortadas: boolean } | null;
}

/* ------------------------------------------------------------ los rótulos ------------------------------------------------------------ */

/** Ficha de color: Nexo la traduce a su tema y Argus Team a sus variables. */
export type TonoRed = 'rojo' | 'ambar' | 'gris';

export const tipoDe = (v: VinculoSiger): string => v.tipo ?? 'sin_tipo';
export const ordenDe = (v: VinculoSiger): number => v.tipo_orden ?? 9;
export const textoTipo = (v: VinculoSiger): string => v.tipo_texto ?? 'Otra relación';
/**
 * El tono de una relación por su tipo, con el mismo criterio que el tono del buscador (nexo.relacion_ofac_tono; escala de riesgo,
 * 20261006140000): capital, cargo, poder, familia y dinero, rojo (Muy alto y Alto); lo demás, ámbar (Medio). Ninguna relación es gris.
 * Antes iba por el orden (nexo.tipo_liga_orden), que junta en el mismo peso el dinero (Alto) y la vigilancia de las cuentas (Medio).
 * Copia vigilada de la regla de la base; la prueba red_piezas_test.ts compara las claves.
 */
export const TIPOS_ROJOS: ReadonlySet<string> = new Set(['en_lista_ofac', 'coincide_ofac', 'socio_de_sancionada', 'administra_sancionada',
  'sancionado_es_socio', 'sancionado_la_administra', 'misma_empresa', 'apoderado_de_sancionado', 'sancionado_es_apoderado',
  'operacion_de_acciones', 'hijo_de_sancionado', 'padre_de_sancionado', 'conyuge_de_sancionado', 'hermano_de_sancionado',
  'familiar_de_sancionado', 'relacion_financiera', 'presto_a_sancionado', 'sancionado_le_presto',
  // corte 2b de los certificados del SAT (20261016233000)
  'representa_ante_sat', 'sancionado_lo_representa_ante_sat']);
export const tonoDeTipo = (tipo: string): TonoRed => (TIPOS_ROJOS.has(tipo) ? 'rojo' : 'ambar');
export const tonoDe = (v: VinculoSiger): TonoRed => tonoDeTipo(tipoDe(v));
/** Contexto que no se muestra de entrada: relación indirecta y autoridad o litigio. */
export const OCULTOS_DE_ENTRADA: ReadonlySet<string> = new Set(['indirecta', 'autoridad_litigio']);

export const ROL_ES: Record<string, string> = {
  socio: 'socio', accionista_adquirente: 'compra acciones', accionista_transmitente: 'vende acciones',
  administrador_unico: 'administrador único', presidente_consejo: 'presidente del consejo', secretario_consejo: 'secretario',
  tesorero: 'tesorero', consejero: 'consejero', comisario: 'vigila las cuentas', gerente_director: 'gerente / director',
  representante_legal: 'tiene un poder', apoderado: 'tiene un poder', delegado: 'delegado', liquidador: 'liquidador',
  interventor: 'interventor', depositario: 'depositario', acreedor: 'acreedor', deudor: 'deudor', aval_garante: 'aval',
  demandante: 'demandante', demandado: 'demandado', comerciante_individual: 'comerciante', otro: 'otro',
  sucesion_en_cargo: 'sucede en el cargo',
  conyuge: 'cónyuge', padre_madre_de: 'padre/madre de', hijo_de: 'hijo(a) de', hermano_de: 'hermano(a) de',
  otro_familiar: 'familiar', transmite_acciones_a: 'transmite acciones a', representa_a: 'representa a',
  apoderado_de: 'tiene un poder de', mismo_domicilio: 'mismo domicilio', aval_de: 'aval de', acreedor_de: 'acreedor de',
  sucede_a: 'sucede a', fusiona_con: 'se fusiona con', cambia_denominacion_de: 'cambió de nombre',
};
export const CAMPO_ES: Record<string, string> = {
  rfc: 'RFC', curp: 'CURP', fecha_nacimiento: 'Fecha de nacimiento', edad_declarada: 'Edad declarada',
  lugar_nacimiento: 'Lugar de nacimiento', nacionalidad: 'Nacionalidad', sexo: 'Sexo', estado_civil: 'Estado civil',
  regimen_matrimonial: 'Régimen matrimonial', ocupacion: 'Ocupación', domicilios: 'Domicilio', otros_identificadores: 'Otros identificadores',
};
export const ORIGEN_ES: Record<string, string> = { impreso: 'impreso', derivado_rfc_curp: 'derivado del RFC/CURP', calculado: 'calculado' };

/** Cómo se escribe una fecha del acta en cada aplicación («10 sep 2024» en Nexo, «10 de septiembre de 2024» en Argus Team). */
export type FormatoFecha = (iso: string) => string;

export function corto(s: string | null | undefined, n = 36): string {
  const t = (s ?? '').trim();
  return t.length > n ? `${t.slice(0, n - 1)}…` : t;
}
export function vecino(l: LazoSiger, actor: string): string {
  if (l.tipo === 'participacion') return l.persona === actor ? l.sociedad : l.persona;
  return l.de === actor ? l.a : l.de;
}
export function terminado(l: LazoSiger): boolean {
  return l.tipo === 'participacion' && l.estado !== 'vigente_segun_registro';
}
export function etiquetaLazo(l: LazoSiger, fecha: FormatoFecha): string {
  const dia = (iso: string | null | undefined) => (iso ? fecha(iso) : '?');
  if (l.tipo === 'participacion') {
    const estado = l.estado === 'vigente_segun_registro' ? `desde ${dia(l.desde)}` : `${dia(l.desde)}–${dia(l.hasta)}`;
    return `${ROL_ES[l.rol] ?? l.rol} · ${estado}`;
  }
  return `${ROL_ES[l.relacion] ?? l.relacion}${l.base === 'inferida' ? ' (inferido)' : ''}`;
}
/** Etiqueta del lazo vista desde el nodo: si el nodo es la sociedad, dice quién ocupa el cargo en ella («socio: W&G ARQUITECTOS»), para no
 * leerlo al revés. */
export function etiquetaNodo(l: LazoSiger, nodo: string, actores: Record<string, ActorGrafoSiger>, fecha: FormatoFecha): string {
  if (l.tipo === 'participacion' && l.sociedad === nodo && l.persona !== nodo) {
    const dia = (iso: string | null | undefined) => (iso ? fecha(iso) : '?');
    const estado = l.estado === 'vigente_segun_registro' ? `desde ${dia(l.desde)}` : `${dia(l.desde)}–${dia(l.hasta)}`;
    return `${ROL_ES[l.rol] ?? l.rol}: ${actores[l.persona]?.nombre ?? '?'} · ${estado}`;
  }
  if (l.tipo === 'relacion' && l.a === nodo && l.de !== nodo) {
    return `${actores[l.de]?.nombre ?? '?'} · ${etiquetaLazo(l, fecha)}`;
  }
  return etiquetaLazo(l, fecha);
}

/* ------------------------------------------------------------ las listas del SAT ------------------------------------------------------------ */

/** Etiqueta de una lista del SAT (antes en el tema de Nexo, que la reexporta). */
export function etiquetaListaSat(lista: string, categoria: string): string {
  const listaEtq = lista === '69b' ? 'Facturas falsas:' : lista === '69b_bis' ? 'Pérdidas fiscales:' : '69';
  const catEtq = lista === '69b' && categoria === 'definitivo'
    ? 'confirmada por el SAT'
    : lista === '69b' && categoria === 'presunto'
    ? 'presunta'
    : lista === '69b_bis' && categoria === 'perdidas_fiscales'
    ? 'transmitidas indebidamente'
    : categoria.replace(/_/g, ' ');
  return `${listaEtq} ${catEtq}`;
}

/**
 * Listas SAT de más a menos grave: [lista/categoría, chip, texto, gravedad]. 2026-10-02 (etapa 1b): el chip en oración normal y corto,
 * para que quepa en el nodo («SAT: facturas falsas», «SAT: no localizado»), nunca en mayúsculas; el texto completo va en el detalle.
 */
export const SAT_RED: [string, string, string, 'alta' | 'media' | 'baja'][] = [
  ['69b/definitivo', 'SAT: facturas falsas', 'lista del SAT de empresas que emiten facturas falsas: confirmada por el SAT', 'alta'],
  ['69b/presunto', 'SAT: facturas falsas presunta', 'lista del SAT de empresas que emiten facturas falsas: presunta', 'alta'],
  ['69/sentencia_delito_fiscal', 'SAT: delito fiscal', 'sentencia por delito fiscal (Art. 69)', 'alta'],
  ['69b/sentencia_favorable', 'SAT: sentencia favorable', 'lista del SAT de empresas que emiten facturas falsas, con sentencia favorable al contribuyente', 'media'],
  ['69/no_localizado', 'SAT: no localizado', 'contribuyente no localizado (Art. 69)', 'media'],
  ['69/csd_sin_efectos', 'SAT: sello sin efectos', 'certificado de sello digital sin efectos', 'media'],
  ['69/firme', 'SAT: crédito firme', 'crédito fiscal firme', 'media'],
  ['69/exigible', 'SAT: crédito exigible', 'crédito fiscal exigible', 'media'],
  ['69b/desvirtuado', 'SAT: desvirtuado', 'lista del SAT de empresas que emiten facturas falsas: desvirtuado', 'baja'],
];
export function satOrden(f: FlagSatSiger): number {
  const i = SAT_RED.findIndex(([k]) => k === `${f.lista}/${f.categoria}`);
  return i < 0 ? 99 : i;
}
export function satTexto(f: FlagSatSiger): string {
  return SAT_RED.find(([k]) => k === `${f.lista}/${f.categoria}`)?.[2] ?? etiquetaListaSat(f.lista, f.categoria);
}
/** El tono de una lista del SAT en el detalle: las tres más graves en rojo, las demás en ámbar. */
export const tonoSat = (f: FlagSatSiger): TonoRed => (satOrden(f) < 3 ? 'rojo' : 'ambar');
/** Cómo coincide una fila del SAT con la persona o empresa (texto del detalle). */
export function satCoincidencia(f: FlagSatSiger): string {
  return f.metodo === 'rfc' ? `coincide por RFC ${f.rfc ?? ''}`
    : f.metodo === 'denominacion' ? 'coincide por denominación exacta (verificar RFC)'
    : f.metodo === 'rfc_ofac' ? `coincide por el RFC que publica OFAC (${f.rfc ?? ''})`
    : `posible: misma raíz de RFC (${f.rfc ?? ''}) y nombre compatible; falta confirmar con el RFC completo`;
}

/* ------------------------------------------------------------ las señales de un nodo ------------------------------------------------------------ */

export interface Chip { t: string; tono: TonoRed; lleno?: boolean; ayuda?: string }

export function satChip(fs: readonly FlagSatSiger[] | undefined): Chip | null {
  const todos = [...(fs ?? [])].sort((a, b) => satOrden(a) - satOrden(b));
  const seguros = todos.filter((x) => x.metodo !== 'rfc_raiz_nombre');
  const f = seguros[0] ?? todos[0];
  if (!f) return null;
  const def = SAT_RED.find(([k]) => k === `${f.lista}/${f.categoria}`);
  if (f.metodo === 'rfc_raiz_nombre') return { t: `¿${def?.[1] ?? `SAT: ${f.categoria.replace(/_/g, ' ')}`}?`, tono: 'ambar' };
  const tono: TonoRed = def?.[3] === 'alta' ? 'rojo' : def?.[3] === 'media' ? 'ambar' : 'gris';
  return { t: def?.[1] ?? `SAT: ${f.categoria.replace(/_/g, ' ')}`, tono, lleno: def?.[3] === 'alta' };
}

/** Rótulos de los nodos (Bernardo, 2026-10-01; leyenda aprobada por delegación el 2026-10-04). */
export const NODO_RED = {
  enLista: 'En la lista de OFAC',
  quizaEnLista: '¿En la lista de OFAC?',
  contradice: '≠ OFAC',
  cincuenta: 'Sancionados: 50 %+ de acciones',
  declara: 'OFAC declara la relación',
  // Revisión 4d (I3): quien salió de la lista. El primero ya está aprobado (4c); el segundo, por aprobar, con el patrón «¿En la lista de OFAC?».
  estuvo: 'Estuvo en la lista de OFAC',
  quizaEstuvo: '¿Estuvo en la lista de OFAC?',
} as const;

/**
 * El rótulo de una identidad frente a la lista (nodo, detalle y «¿Quién es…?»): «En la lista de OFAC» (rojo) sólo corroborada; confirmada,
 * alta o media sin corroborar, «¿En la lista de OFAC?» (ámbar); contradictoria o descartada, «≠ OFAC». Quien salió de la lista dice
 * «Estuvo…» (revisión I3), nunca en rojo.
 */
export function rotuloIdentidad(o: { nivel: string; corroborada?: boolean | null; en_lista?: string | null }): Chip {
  const salio = o.en_lista === 'retirada';
  if (rojoDeIdentidad(o)) return salio ? { t: NODO_RED.estuvo, tono: 'ambar' } : { t: NODO_RED.enLista, tono: 'rojo', lleno: true };
  if (o.nivel === 'confirmada' || o.nivel === 'alta' || o.nivel === 'media') return { t: salio ? NODO_RED.quizaEstuvo : NODO_RED.quizaEnLista, tono: 'ambar' };
  return { t: NODO_RED.contradice, tono: 'gris' };
}

/** Revisión 4d (M4): una etiqueta corta en mayúsculas («NOMBRE + F. NAC.») en forma de oración, con las siglas permitidas en mayúsculas. */
export function formaDeOracion(t: string): string {
  const s = t.toLowerCase().replace(/\b(ofac|rfc|curp|sat)\b/g, (m) => m.toUpperCase());
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * Las señales de un nodo: «En la lista de OFAC» (rojo) sólo con identidad corroborada (rojoDeIdentidad); confirmada, alta o media sin
 * corroborar, «¿En la lista de OFAC?» (ámbar); la lista del SAT; 50 % en manos de sancionados; OFAC declara la relación; y «posible» si
 * la relación llega por un ancla sin corroborar (4d, la marca del buscador).
 */
export function chipsActor(a: ActorGrafoSiger | undefined, entidadRaiz: string, v: VinculoSiger | null, declaradaOfac = false): Chip[] {
  const out: Chip[] = [];
  const o = (a?.ofac ?? []).find((x) => x.entidad_id !== entidadRaiz) ?? (a?.ofac ?? [])[0];
  if (o) out.push(rotuloIdentidad(o));
  const sc = satChip(a?.sat);
  if (sc) out.push(sc);
  if ((a?.propiedad?.pct_ofac ?? 0) >= 50) out.push({ t: NODO_RED.cincuenta, tono: 'rojo' });
  if (declaradaOfac) out.push({ t: NODO_RED.declara, tono: 'rojo' });
  if (v && v.ancla_corroborada === false) out.push({ t: MARCA_POSIBLE, tono: 'ambar', ayuda: MARCA_POSIBLE_AYUDA });
  return out;
}
/** ¿El nodo se pinta como de la lista (fondo y borde rojos)? Sólo con identidad corroborada y si sigue en la lista. */
export const nodoEnLista = (a: ActorGrafoSiger | undefined): boolean => (a?.ofac ?? []).some((o) => rojoDeIdentidad(o) && o.en_lista !== 'retirada' && o.en_lista !== 'no_esta');

/** Las señales que caben en el nodo y, si sobran, «+N» al final, con su x. */
export function chipsColocados(chips: readonly Chip[], ancho: number): { chip: Chip; x: number; ancho: number }[] {
  const { x: xs, fuera } = colocarChips(chips.map((c) => c.t), ancho);
  const dentro = chips.slice(0, xs.length).map((chip, i) => ({ chip, x: xs[i]!, ancho: anchoChip(chip.t) }));
  if (!fuera) return dentro;
  const ultimo = dentro[dentro.length - 1];
  const t = `+${String(fuera)}`;
  return [...dentro, { chip: { t, tono: 'gris' }, x: ultimo ? ultimo.x + ultimo.ancho + 4 : 12, ancho: anchoChip(t) }];
}

/* ------------------------------------------------------------ el acomodo ------------------------------------------------------------ */

export const GEOMETRIA_RED = {
  xRaiz: 12, wRaiz: 190, xN1: 250, wN1: 330, xN2: 630, wN2: 330, ancho: 630 + 330 + 16, fila: 54, alto: 46, sep: 14,
} as const;

export function curva(x1: number, y1: number, x2: number, y2: number): string {
  const mx = (x1 + x2) / 2;
  return `M${String(x1)},${String(y1)} C${String(mx)},${String(y1)} ${String(mx)},${String(y2)} ${String(x2)},${String(y2)}`;
}

export interface GrupoRed { tipo: string; texto: string; orden: number; ligas: number }
/** Los grupos de relaciones: los de la base (mismos conteos que la lista); si no vienen, los que traen las relaciones dibujadas. */
export function gruposRed(datos: GrafoSiger): GrupoRed[] {
  if (datos.totales_tipo && datos.totales_tipo.length > 0) {
    return datos.totales_tipo.map((t) => ({ tipo: t.tipo, texto: t.texto ?? 'Otra relación', orden: t.orden, ligas: t.ligas }));
  }
  const m = new Map<string, GrupoRed>();
  for (const v of datos.vinculos) {
    const g = m.get(tipoDe(v)) ?? { tipo: tipoDe(v), texto: textoTipo(v), orden: ordenDe(v), ligas: 0 };
    g.ligas += 1;
    m.set(tipoDe(v), g);
  }
  return [...m.values()].sort((x, y) => x.orden - y.orden || x.texto.localeCompare(y.texto));
}

export interface FilaRed { v: VinculoSiger; hs: VinculoSiger[]; y0: number; n: number }
export interface AcomodoRed { filas: FilaRed[]; sinActa: { r: RelacionOfacSiger; y0: number }[]; lejanos: VinculoSiger[]; alto: number; yRaiz: number }

/**
 * Dónde va cada nodo: la raíz a la izquierda; las relaciones directas (una arista) en la primera columna, de más a menos peso; a la derecha
 * de cada una, quienes llegan a través de ella (dos aristas); abajo, las relaciones que declara OFAC sin acta (punteadas). Lo que no tiene
 * intermediario visible con el filtro va en `lejanos` (se lista aparte).
 */
export function acomodarRed(datos: GrafoSiger, ocultos: ReadonlySet<string>, soloVigentes: boolean): AcomodoRed {
  const A = datos.actores;
  const G = GEOMETRIA_RED;
  const vis = datos.vinculos.filter((v) => !ocultos.has(tipoDe(v)) && (!soloVigentes || v.vigente !== false));
  const n1 = vis.filter((v) => v.aristas === 1)
    .sort((x, y) => ordenDe(x) - ordenDe(y) || Number(A[y.actor_id]?.tipo === 'empresa') - Number(A[x.actor_id]?.tipo === 'empresa') ||
      (A[x.actor_id]?.nombre ?? '').localeCompare(A[y.actor_id]?.nombre ?? ''));
  const idsN1 = new Set(n1.map((v) => v.actor_id));
  const hijos = new Map<string, VinculoSiger[]>();
  for (const v of vis.filter((x) => x.aristas === 2)) {
    const l = v.camino[0];
    const via = l ? vecino(l, v.actor_id) : '';
    if (idsN1.has(via)) hijos.set(via, [...(hijos.get(via) ?? []), v]);
  }
  let y = 12;
  const filas = n1.map((v) => {
    const hs = (hijos.get(v.actor_id) ?? []).sort((a, b) => ordenDe(a) - ordenDe(b) || b.fuerza - a.fuerza);
    const n = Math.max(1, hs.length);
    const f = { v, hs, y0: y, n };
    y += n * G.fila + G.sep;
    return f;
  });
  // Relaciones que declara OFAC y que no están en el grafo del registro (sin acta o fuera del filtro): se dibujan punteadas.
  const sinActa = (datos.relaciones_ofac ?? []).filter((r) => !r.actor_id || !idsN1.has(r.actor_id)).map((r) => {
    const f = { r, y0: y };
    y += G.fila + G.sep;
    return f;
  });
  const ubicados = new Set([...idsN1, ...filas.flatMap((f) => f.hs.map((h) => h.actor_id))]);
  const alto = Math.max(140, y + 8);
  return { filas, sinActa, lejanos: vis.filter((v) => !ubicados.has(v.actor_id)), alto, yRaiz: Math.min(60, alto / 2) };
}
